import { ChangeDetectionStrategy, Component, ElementRef, inject, input, OnDestroy, OnInit, output } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormBase } from 'src/app/_models/form-base';
import { InformationBannerType } from 'src/app/_models/information-banner';
import { ModalService } from 'src/app/_services/modal.service';
import { UnderlinedDropdownFormFieldComponent } from '../../forms/underlined-dropdown-form-field/underlined-dropdown-form-field.component';
import { ButtonComponent } from '../../buttons/button/button.component';
import { ToastComponent } from '../../toast/toast.component';
import { bannerIcon } from 'src/app/_helpers/utils';
import { UnderlinedTextareaFieldComponent } from '../../forms/underlined-textarea-field/underlined-textarea-field.component';
import { MatSlideToggle } from '@angular/material/slide-toggle';

@Component({
  selector: 'app-info-banner-modal',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    ButtonComponent,
    UnderlinedDropdownFormFieldComponent,
    UnderlinedTextareaFieldComponent,
    ToastComponent,
    MatSlideToggle,
  ],
  templateUrl: './info-banner-modal.component.html',
  styleUrl: './info-banner-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InfoBannerModalComponent implements OnInit, OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly modalService = inject(ModalService);
  private readonly element = this.elementRef.nativeElement;
  InformationBannerTypeEnum = InformationBannerType;

  /** Toggle base field (required) */
  displayInformationBanner = input<FormBase<boolean> | undefined>(undefined);

  /** Text base field (optional) */
  informationBanner = input<FormBase<string> | undefined>(undefined);

  /** Select base field (optional) */
  informationBannerType = input<FormBase<InformationBannerType> | undefined>(
    undefined,
  );

  /** Parent form (required)*/
  parentForm = input<FormGroup>();

  /** Modal id (required) */
  id = input<string>();

  /** Success message (optional) */
  successMessage = input<string>();

  /** Error message (optional) */
  errorMessage = input<string>();

  /** Primary button text (required)*/
  primaryButtonText = input<string>();

  /** Primary button id (required) */
  primaryButtonId = input<string>();

  /** Secondary button text (required) */
  secondaryButtonText = input<string>();

  /** Secondary button id (required) */
  secondaryButtonId = input<string>();

  /** Is the modal processing (optional, default: false) */
  isProcessing = input<boolean>(false);

  /** Output event for clicked button on modal */
  modalButtonClicked = output<string>();

  /** Output event for clicked button on modal */
  moduleCheckboxClicked = output<Event>();

  constructor() {
    this.element = this.elementRef.nativeElement;
  }

  ngOnInit(): void {
    // Close modal on background click - only if not currently processing
    this.element.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.className === 'modal--container' && !this.isProcessing()) {
        this.close();
      }
    });
    // add self (this modal instance) to the modal service so it's accessible from controllers
    this.modalService.add(this);
    document.body.appendChild(this.element);
  }

  getIconName(): string {
    return bannerIcon(this.parentForm().get('informationBannerType')?.value);
  }

  updateValue(event: Event): void {
    this.moduleCheckboxClicked.emit(event);
  }

  // open modal
  open(): void {
    this.element.style.display = 'block';
  }

  // close modal
  close(): void {
    // Reset valdiation text value and close modal
    this.element.style.display = 'none';
  }

  buttonEventHandler(valueEmitted: string): void {
    this.modalButtonClicked.emit(valueEmitted);
  }

  // Remove self from modal service when component is destroyed
  ngOnDestroy(): void {
    this.modalService.remove(this.id());
    this.element.remove();
  }
}
